import {
  requestLocalStorageGet,
  requestLocalStorageSet,
  requestLocalStorageRemove,
  requestLocalStorageClear,
  requestLocalStorageKeys,
  requestLocalStorageHas,
  sendLocalStorageResponse,
  sendLocalStorageChanged,
} from '../actions'
import { sendMessage } from '../../../../common/message/sendMessage'
import { listenMessage } from '../../../../common/message/listenMessage'
import { MutableRefObject, useEffect, useRef } from 'react'
import { localStorage as createLocalStorage } from 'helpers/localStorage'

const storage = createLocalStorage()

const emitLocalStorageChanged = (detail: {
  key?: string
  value?: string | null
  operation: 'set' | 'remove' | 'clear'
  source?: 'host' | 'remote'
}) => {
  window.dispatchEvent(
    new CustomEvent('wlsy:remoteLocalStorageChange', {
      detail,
    }),
  )
}

const handleLocalStorageOperation = (
  id: string,
  operation: () => {
    value: string | string[] | boolean | null
    success: boolean
    error?: string
  },
) => {
  try {
    const result = operation()
    return sendLocalStorageResponse({
      id,
      ...result,
    })
  } catch (error) {
    return sendLocalStorageResponse({
      id,
      value: null,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    })
  }
}

export const useHostLocalStorage = (
  iframeRef: MutableRefObject<HTMLIFrameElement | null>,
  isActive: boolean,
) => {
  const unsubscribeRef = useRef<() => void>(() => {})

  useEffect(() => {
    if (!isActive) {
      unsubscribeRef.current()
      return
    }

    const forwardHostStorageChangeToIframe = (event: Event) => {
      const detail = (event as CustomEvent).detail as {
        key?: string
        value?: string | null
        operation: 'set' | 'remove' | 'clear'
        source?: 'host' | 'remote'
      }

      if (detail?.source !== 'host') return

      sendMessage({
        action: sendLocalStorageChanged(detail),
        windowElement: iframeRef.current?.contentWindow ?? null,
        whitelist: [sendLocalStorageChanged],
      })
    }

    window.addEventListener(
      'wlsy:remoteLocalStorageChange',
      forwardHostStorageChangeToIframe as EventListener,
    )

    unsubscribeRef.current = listenMessage({
      sourceWindow: iframeRef.current?.contentWindow ?? null,
      onMessage: (action) => {
        if (requestLocalStorageGet.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<typeof requestLocalStorageGet>
          const response = handleLocalStorageOperation(id, () => {
            const value = storage.getItem(key) ?? null
            return { value, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })
        }

        if (requestLocalStorageSet.match(action)) {
          const {
            payload: { id, key, value },
          } = action as unknown as ReturnType<typeof requestLocalStorageSet>

          const response = handleLocalStorageOperation(id, () => {
            storage.setItem(key, value)
            return { value, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })

          if (response.payload.success) {
            emitLocalStorageChanged({
              key,
              value,
              operation: 'set',
              source: 'remote',
            })
            sendMessage({
              action: sendLocalStorageChanged({
                key,
                value,
                operation: 'set',
                source: 'remote',
              }),
              windowElement: iframeRef.current?.contentWindow ?? window,
              whitelist: [sendLocalStorageChanged],
            })
          }
        }

        if (requestLocalStorageRemove.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<typeof requestLocalStorageRemove>
          const response = handleLocalStorageOperation(id, () => {
            storage.removeItem(key)
            return { value: null, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })

          if (response.payload.success) {
            emitLocalStorageChanged({
              key,
              value: null,
              operation: 'remove',
              source: 'remote',
            })
            sendMessage({
              action: sendLocalStorageChanged({
                key,
                value: null,
                operation: 'remove',
                source: 'remote',
              }),
              windowElement: iframeRef.current?.contentWindow ?? window,
              whitelist: [sendLocalStorageChanged],
            })
          }
        }

        if (requestLocalStorageClear.match(action)) {
          const {
            payload: { id, prefix },
          } = action as unknown as ReturnType<typeof requestLocalStorageClear>
          const response = handleLocalStorageOperation(id, () => {
            if (prefix) {
              storage
                .keys()
                .filter((k) => k.startsWith(prefix))
                .forEach((k) => storage.removeItem(k))
            } else {
              storage.clear()
            }
            return { value: null, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })

          if (response.payload.success) {
            emitLocalStorageChanged({ operation: 'clear', source: 'remote' })
            sendMessage({
              action: sendLocalStorageChanged({
                operation: 'clear',
                source: 'remote',
              }),
              windowElement: iframeRef.current?.contentWindow ?? window,
              whitelist: [sendLocalStorageChanged],
            })
          }
        }

        if (requestLocalStorageKeys.match(action)) {
          const {
            payload: { id },
          } = action as unknown as ReturnType<typeof requestLocalStorageKeys>
          const response = handleLocalStorageOperation(id, () => {
            const value = storage.keys()
            return { value, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })
        }

        if (requestLocalStorageHas.match(action)) {
          const {
            payload: { id, key },
          } = action as unknown as ReturnType<typeof requestLocalStorageHas>
          const response = handleLocalStorageOperation(id, () => {
            const value = storage.has(key)
            return { value, success: true }
          })

          sendMessage({
            action: response,
            windowElement: iframeRef.current?.contentWindow ?? window,
            whitelist: [sendLocalStorageResponse],
          })
        }
      },
      whitelist: [
        requestLocalStorageGet,
        requestLocalStorageSet,
        requestLocalStorageRemove,
        requestLocalStorageClear,
        requestLocalStorageKeys,
        requestLocalStorageHas,
      ],
    })

    return () => {
      unsubscribeRef.current()
      window.removeEventListener(
        'wlsy:remoteLocalStorageChange',
        forwardHostStorageChangeToIframe as EventListener,
      )
    }
  }, [isActive])
}
